import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { filter, find } from 'ramda';
import Button from '~~elements/Button';
import Modal from '~~elements/Modal';
import Multilingual from '~~utils/Multilingual';
import PreSVG from '../assets/chevron-left.svg';
import NextSVG from '../assets/chevron-right.svg';
import { SchedulePanelDiv, ModalFilterDiv, winnerTeamColor, loserTeamColor } from '../styled';
import {
  finishedGameBGColor,
  finishedCriticalGameBGColor,
  finishedMissingGameBGColor,
  playingGameBGColor,
} from '../styled';
import moment from 'moment';

const CalendarDiv = styled.div`
  width: 100%;
  border: #eee 1px solid;
  text-align: center;
  font-size: 0.9rem;
  overflow-x: auto;
  .week {
    width: 100%;
    min-width: 35rem;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: #eee 1px solid;
    :last-child {
      border-bottom: none;
    }

    .day {
      border-right: #eee 1px solid;
      width: 100%;
      :last-child {
        border-right: none;
      }
      &.none-current-month {
        opacity: 0.9;
      }

      &--header {
        display: block;
        &.clickable {
          cursor: pointer;
        }
      }
      &--games {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        min-height: 2rem;
        max-height: 25rem;
        overflow-y: auto;
        ::-webkit-scrollbar {
          width: 2px;
        }
      }
    }

    &.collapse {
      .day {
        &--games {
          max-height: unset;
        }
      }
    }
  }
`;

const GameInDayDiv = styled.div`
  width: 100%;
  border: #eee 1px solid;
  border-radius: 10px;
  margin-bottom: 5px;

  cursor: pointer;
  label {
    cursor: pointer;
  }

  &.finished {
    background-color: ${finishedGameBGColor};
    &.CRITICAL {
      background-color: ${finishedCriticalGameBGColor};
    }
    &.PITCH_MISSING {
      background-color: ${finishedMissingGameBGColor};
    }
  }
  &.playing {
    background-color: ${playingGameBGColor};
  }
  &.problem {
    background-color: #ebc85c;
  }
  &.status-SUSPENDED,
  &.status-POSTPONED {
    background-color: #f6e4f6;
  }

  > h5 {
    width: 100%;
    padding: 0.1rem 0;
    margin: 0;
    border-bottom: #eee 1px solid;
  }
  .two-teams {
    width: 100%;
    > label {
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      padding: 0 0.1rem;
      :last-child {
        border-top: #eee 1px solid;
      }
      &.win {
        color: ${winnerTeamColor};
      }
      &.lose {
        color: ${loserTeamColor};
      }
    }
  }
`;

export const StartInWeekStorageKey = 'management::tournament::calendar::startInWeek';

const dayInWeekWording = {
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
  7: 'SUNDAY',
};

const getDefaultYearMonth = (games) => {
  const today = moment();
  if (games.length === 0) {
    return {
      year: today.get('years'),
      month: today.get('months'),
    };
  }

  const firstGameDate = moment(games[0].info.scheduled_start_at);
  const lastGameDate = moment(games[games.length - 1].info.scheduled_start_at);
  if (moment(firstGameDate).isBefore(today) && moment(today).isBefore(lastGameDate)) {
    return {
      year: today.get('years'),
      month: today.get('months'),
    };
  }

  if (moment(today).isBefore(firstGameDate)) {
    return {
      year: firstGameDate.get('years'),
      month: firstGameDate.get('months'),
    };
  }

  return {
    year: lastGameDate.get('years'),
    month: lastGameDate.get('months'),
  };
};

const ScheduleCalendar = ({
  games,
  gameLocations,
  handleGameFilterOption,
  selectedLocations,
  setGameListCollapse,
  gameListCollapse,
  setSelectedGame,
}) => {
  const [currentYear, setCurrentYear] = useState(getDefaultYearMonth(games).year);
  const [currentMonth, setCurrentMonth] = useState(getDefaultYearMonth(games).month);
  const [startInWeek] = useState(
    ['1', '2', '3', '4', '5', '6', '7'].includes(localStorage.getItem(StartInWeekStorageKey))
      ? parseInt(localStorage.getItem(StartInWeekStorageKey))
      : 1,
  );
  const [daysInCalendar, setDaysInCalendar] = useState([]);

  const [isGameFilterOpen, setGameFilterOpen] = useState(false);

  useEffect(() => {
    // 下個月的 0 = 這個月的最後一天
    const lastDate = new Date(currentYear, currentMonth, 0);
    const lastDayInWeek = lastDate.getDay() === 0 ? 7 : lastDate.getDay();
    const daysInMonth = lastDate.getDate();

    // 每列最後一天是星期幾
    let endInWeek = (startInWeek + 6) % 7;
    if (endInWeek === 0) {
      endInWeek = 7;
    }

    // 下個月/上個月要顯示多少天進來
    let nextMonthAppend = endInWeek - lastDayInWeek;
    if (nextMonthAppend < 0) {
      nextMonthAppend += 7;
    }
    const preMonthPrepend = (7 - ((daysInMonth - (7 - nextMonthAppend)) % 7)) % 7;

    const firstDateInCalendar = new Date(currentYear, currentMonth - 1, 1 - preMonthPrepend);
    const lastDateInCalendar = new Date(currentYear, currentMonth, nextMonthAppend);
    const dayAmount = moment(lastDateInCalendar).diff(firstDateInCalendar, 'days') + 1;

    const newDaysInCalendar = [];
    for (let i = 0; i < dayAmount; i++) {
      newDaysInCalendar.push(moment(firstDateInCalendar).add(i, 'days').toDate());
    }
    setDaysInCalendar(newDaysInCalendar);
  }, [currentMonth, currentYear, startInWeek]);

  const handleGameFilterClick = () => {
    setGameFilterOpen(!isGameFilterOpen);
  };

  const handleCurrentMonthClick = () => {
    setCurrentYear(new Date().getFullYear());
    setCurrentMonth(new Date().getMonth() + 1);
  };

  const handleGameMonthClick = (isFirst) => () => {
    // API 拿出來的比賽預設是由時間近到時間遠
    const game = games[isFirst ? games.length - 1 : 0];
    const gameDate = new Date(game.info.scheduled_start_at);
    setCurrentYear(gameDate.getFullYear());
    setCurrentMonth(gameDate.getMonth() + 1);
  };

  const handleMonthClick = (add) => () => {
    let newYear = currentYear;
    let newMonth = currentMonth + add;
    if (newMonth < 1) {
      newYear--;
      newMonth = 12;
    } else if (newMonth > 12) {
      newYear++;
      newMonth = 1;
    }

    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
  };

  const handleWeekCollapse = () => {
    setGameListCollapse(!gameListCollapse);
  };

  const getGamesInDay = (targetDate) => {
    const targetDateTime = targetDate.getTime();
    const nextDateTime = moment(targetDate).add(1, 'days').toDate().getTime();

    const matchFilter = (game) => {
      if (selectedLocations.length > 0 && !selectedLocations.includes(game.info.location_uniqid)) {
        return false;
      }
      return true;
    };

    const shouldInDay = (game) => {
      const gameTime = moment(game.info.scheduled_start_at).toDate().getTime();
      return targetDateTime <= gameTime && gameTime < nextDateTime;
    };

    const getTeamClassName = (game, side) => {
      if (game.info.status !== 'FINISHED') {
        return '';
      }

      return game.info.winner_side === side ? 'win' : 'lose';
    };

    const handleGameClick = (gameUniqid) => () => {
      setSelectedGame(gameUniqid);
    };

    return filter(matchFilter, games)
      .filter(shouldInDay, games)
      .reverse()
      .map((game) => (
        <GameInDayDiv
          key={game.uniqid}
          className={`${
            game.info.status === 'FINISHED'
              ? 'finished'
              : game.info.status === 'PLAYING'
              ? 'playing'
              : `status-${game.info.status}`
          } ${game.info.stats_status}`}
          onClick={handleGameClick(game.uniqid)}
        >
          <h5>{`G${game.seq} / ${moment(game.info.scheduled_start_at).format('HH:mm')}`}</h5>
          <div className="two-teams">
            <label className={getTeamClassName(game, 'AWAY')}>
              {!game.away.team ? Multilingual(`GAME.TEAM_NONE`) : game.away.team}
            </label>
            <label className={getTeamClassName(game, 'HOME')}>
              {!game.home.team ? Multilingual(`GAME.TEAM_NONE`) : game.home.team}
            </label>
          </div>
        </GameInDayDiv>
      ));
  };

  return (
    <Fragment>
      <SchedulePanelDiv>
        <div className="schedule-panel schedule-panel--start">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleGameFilterClick}>
            {Multilingual(`TOURNAMENT.FILTER`)}
          </Button>
          <label>
            {`${Multilingual(`TOURNAMENT.GAME_LOCATION`)}：`}
            {selectedLocations.length === 0
              ? Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)
              : selectedLocations
                  .map((locationUniqid) => {
                    const location = find((l) => l.uniqid === locationUniqid, gameLocations);
                    return !location ? '' : location.name;
                  })
                  .join(', ')}
          </label>
        </div>
      </SchedulePanelDiv>
      <SchedulePanelDiv>
        <div className="schedule-panel schedule-panel--start">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleCurrentMonthClick}>
            {Multilingual(`CALENDAR.CURRENT_MONTH`)}
          </Button>
          {games.length > 0 && (
            <Fragment>
              <Button width="unset" color="transparentWithBorder" nowrap onClick={handleGameMonthClick(true)}>
                {Multilingual(`TOURNAMENT.FIRST_GAME`)}
              </Button>
              <Button width="unset" color="transparentWithBorder" nowrap onClick={handleGameMonthClick(false)}>
                {Multilingual(`TOURNAMENT.LAST_GAME`)}
              </Button>
            </Fragment>
          )}
          <h2 className="schedule-panel">
            <Button className="pre-next-control" color="transparent" nowrap onClick={handleMonthClick(-1)}>
              <img src={PreSVG} alt="pre" />
            </Button>
            {moment(`${currentYear}-${currentMonth >= 10 ? '' : '0'}${currentMonth}-01`).format('YYYY/MM')}
            <Button className="pre-next-control" color="transparent" nowrap onClick={handleMonthClick(1)}>
              <img src={NextSVG} alt="next" />
            </Button>
          </h2>
        </div>
        <div className="schedule-panel schedule-panel--end">
          <Button width="unset" color="transparentWithBorder" nowrap onClick={handleWeekCollapse}>{`${
            gameListCollapse ? 'V' : 'X'
          } ${Multilingual(`CALENDAR.COLLAPSE`)}`}</Button>
        </div>
      </SchedulePanelDiv>
      <SchedulePanelDiv>
        <label>{`*${Multilingual(`TOURNAMENT.CALENDAR_CREATE_GAME_HINT`)}`}</label>
      </SchedulePanelDiv>
      <CalendarDiv>
        <div className="week">
          {new Array(7).fill('').map((_, dayInWeekIndex) => {
            let dayInWeek = startInWeek + dayInWeekIndex;
            if (dayInWeek > 7) {
              dayInWeek -= 7;
            }
            return (
              <div key={dayInWeekIndex} className="day">
                <label className="day--header">{Multilingual(`CALENDAR.${dayInWeekWording[dayInWeek]}`)}</label>
              </div>
            );
          })}
        </div>
        {new Array(daysInCalendar.length / 7).fill('').map((_, weekIndex) => (
          <div key={weekIndex} className={`week ${gameListCollapse ? 'collapse' : ''}`}>
            {new Array(7).fill('').map((_, dayInWeekIndex) => {
              const date = daysInCalendar[7 * weekIndex + dayInWeekIndex];
              const display = date.getDate() === 1 ? moment(date).format('MM/DD') : moment(date).format('DD');
              return (
                <div
                  key={`${weekIndex}-${dayInWeekIndex}`}
                  className={`day ${date.getMonth() + 1 === currentMonth ? '' : 'none-current-month'}`}
                >
                  <label className="day--header">{display}</label>
                  <div className="day--games">{getGamesInDay(date)}</div>
                </div>
              );
            })}
          </div>
        ))}
      </CalendarDiv>
      <Modal.Modal size="lg" isOpen={isGameFilterOpen} outterClickBack onClickBack={handleGameFilterClick}>
        <Modal.ModalHeader hasBack onClickBack={handleGameFilterClick}>
          {Multilingual(`TOURNAMENT.FILTER`)}
        </Modal.ModalHeader>
        <Modal.ModalBody>
          <ModalFilterDiv>
            <div className="filter-item">
              <h3 className="filter-item--name">{Multilingual(`TOURNAMENT.GAME_LOCATION`)}</h3>
              <div className="filter-item--options">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedLocations.length === 0}
                    onChange={handleGameFilterOption('LOCATION', 'ALL')}
                  />
                  {Multilingual(`TOURNAMENT.GAME_SELECT_ALL`)}
                </label>
                {gameLocations.map((location) => (
                  <label key={location.name}>
                    <input
                      type="checkbox"
                      checked={selectedLocations.includes(location.uniqid)}
                      onChange={handleGameFilterOption('LOCATION', location.uniqid)}
                    />
                    {location.name}
                  </label>
                ))}
              </div>
            </div>
          </ModalFilterDiv>
        </Modal.ModalBody>
        <Modal.ModalFooter>
          <Button color="transparent" width="unset" onClick={handleGameFilterClick}>
            {Multilingual(`COMMON.CLOSE`)}
          </Button>
        </Modal.ModalFooter>
      </Modal.Modal>
    </Fragment>
  );
};

export default ScheduleCalendar;
